import type { InviteViewPhase } from "./inviteTypes";
import { isPrixiInvitePhase, mapPrixiPhaseToBase } from "./invitePhaseUtils";

/** Layout tokens — hero/logo only; other slots size to content. */
export const INVITE_SECTION = {
  hero: 80,
} as const;

export type InviteBodyKind =
  | "instructions"
  | "meta-connect-instructions"
  | "meta-mapping-instructions"
  | "meta-page-id-fallback"
  | "disconnect-info"
  | "disconnect-info-already"
  | "inactive-context"
  | "disconnected-hint"
  | "none";

export type InviteErrorsKind = "oauth" | "disconnect" | "none";

export type InviteActionsKind =
  | "connect"
  | "disconnect"
  | "spinner"
  | "retry"
  | "skeleton"
  | "none";

export interface InviteSectionVisibility {
  account: boolean;
  accountSkeleton: boolean;
  body: InviteBodyKind[];
  errors: InviteErrorsKind;
  actions: InviteActionsKind;
}

const CONNECT_BODY: InviteBodyKind[] = ["instructions"];
const META_CONNECT_BODY: InviteBodyKind[] = ["meta-connect-instructions"];
const META_MAPPING_BODY: InviteBodyKind[] = ["meta-mapping-instructions"];
const META_PAGE_ID_FALLBACK_BODY: InviteBodyKind[] = ["meta-page-id-fallback"];

function getPodscopeSectionVisibility(
  phase: InviteViewPhase,
  isPlatformSuccess: boolean,
): InviteSectionVisibility {
  switch (phase) {
    case "loading":
      return {
        account: false,
        accountSkeleton: false,
        body: [],
        errors: "none",
        actions: "skeleton",
      };
    case "revalidating":
      return {
        account: isPlatformSuccess,
        accountSkeleton: isPlatformSuccess,
        body: [],
        errors: "none",
        actions: "spinner",
      };
    case "connect":
    case "connect_error":
      return {
        account: false,
        accountSkeleton: false,
        body: CONNECT_BODY,
        errors: "oauth",
        actions: "connect",
      };
    case "meta_page_id":
      return {
        account: false,
        accountSkeleton: false,
        body: META_PAGE_ID_FALLBACK_BODY,
        errors: "oauth",
        actions: "connect",
      };
    case "meta_podcast_select":
      return {
        account: false,
        accountSkeleton: false,
        body: META_CONNECT_BODY,
        errors: "oauth",
        actions: "connect",
      };
    case "meta_mapping":
      return {
        account: false,
        accountSkeleton: false,
        body: META_MAPPING_BODY,
        errors: "oauth",
        actions: "connect",
      };
    case "connecting":
      return {
        account: false,
        accountSkeleton: false,
        body: [],
        errors: "oauth",
        actions: "spinner",
      };
    case "success":
      return {
        account: true,
        accountSkeleton: false,
        body: ["disconnect-info"],
        errors: "disconnect",
        actions: "disconnect",
      };
    case "already_connected":
      return {
        account: true,
        accountSkeleton: false,
        body: ["disconnect-info-already"],
        errors: "disconnect",
        actions: "disconnect",
      };
    case "disconnected":
      return {
        account: false,
        accountSkeleton: false,
        body: ["disconnected-hint"],
        errors: "none",
        actions: "none",
      };
    case "expired":
    case "unavailable":
      return {
        account: false,
        accountSkeleton: false,
        body: ["inactive-context"],
        errors: "none",
        actions: "none",
      };
    case "fatal":
      return {
        account: false,
        accountSkeleton: false,
        body: [],
        errors: "none",
        actions: "retry",
      };
    default:
      return {
        account: false,
        accountSkeleton: false,
        body: [],
        errors: "none",
        actions: "none",
      };
  }
}

function applyPrixiSectionOverrides(
  phase: InviteViewPhase,
  sections: InviteSectionVisibility,
): InviteSectionVisibility {
  if (phase === "prixi_success" || phase === "prixi_already_connected") {
    return {
      ...sections,
      actions: "none",
      errors: "none",
    };
  }
  return sections;
}

export function getInviteSectionVisibility(
  phase: InviteViewPhase,
  opts?: { isPlatformSuccess?: boolean },
): InviteSectionVisibility {
  const isPlatformSuccess = opts?.isPlatformSuccess ?? false;
  const basePhase = isPrixiInvitePhase(phase)
    ? mapPrixiPhaseToBase(phase)
    : phase;
  const sections = getPodscopeSectionVisibility(basePhase, isPlatformSuccess);

  if (isPrixiInvitePhase(phase)) {
    return applyPrixiSectionOverrides(phase, sections);
  }

  return sections;
}
